import React from 'react'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory, {
  PaginationProvider,
  SizePerPageDropdownStandalone,
  PaginationListStandalone,
} from 'react-bootstrap-table2-paginator'
import filterFactory, { textFilter, selectFilter } from 'react-bootstrap-table2-filter'
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min'
import { CSVExport } from 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min'
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css'
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css'
import 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css'

import { useDispatch, useSelector } from 'react-redux'
import { approveCompany, resolveCompany } from '../../actions/keeperReconActions'
import { UncontrolledTooltip } from 'reactstrap'
import CompanyMappingModal from './CompanyMappingModal'

const { SearchBar } = Search
const { ExportCSVButton } = CSVExport

const statusOptions = {
  AwaitingSync: 'Awaiting Sync',
  NoChanges: 'No Changes',
  Pending: 'Pending',
  Flagged: 'Flagged',
  Blocked: 'Blocked',
  Approved: 'Approved',
  Completed: 'Completed',
}

const statusColors = {
  AwaitingSync: '#adb5bd',
  NoChanges: '#6c757d',
  Pending: '#0d6efd',
  Flagged: '#ffc107',
  Blocked: '#dc3545',
  Approved: '#17a2b8',
  Completed: '#28a745',
}

const exportUserCsv = (row) => {
  if (!row.userEmails || row.userEmails.length === 0) return

  const csvContent = 'Email\n' + row.userEmails.join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${(row.companyName || row.keeperEnterpriseName || 'users').replace(/[^a-zA-Z0-9]/g, '_')}_users.csv`
  link.click()
  URL.revokeObjectURL(url)
}

const KeeperReconTable = ({ rows }) => {
  const dispatch = useDispatch()
  const updatingRowId = useSelector((state) => state.keeperRecon.updatingRowId)
  const [filteredData, setFilteredData] = React.useState(rows)
  const [mappingModalRow, setMappingModalRow] = React.useState(null)

  const columns = [
    {
      dataField: 'companyName',
      text: 'Company',
      sort: true,
      filter: textFilter({ placeholder: 'Filter...' }),
      formatter: (cell, row) => cell || row.keeperEnterpriseName || '-',
    },
    {
      dataField: 'companyType',
      text: 'Type',
      sort: true,
      filter: selectFilter({
        options: { Managed: 'Managed', Essentials: 'Essentials' },
        placeholder: 'Filter...',
      }),
      formatter: (cell) => cell || '-',
    },
    {
      dataField: 'keeperEnterpriseName',
      text: 'Keeper Company',
      sort: true,
      formatter: (cell, row) => {
        if (!cell && !row.hasSyncData) return <span className="text-muted">Not synced</span>

        return (
          <span
            style={{ cursor: 'pointer', borderBottom: '1px dashed #6c757d' }}
            onClick={() => setMappingModalRow(row)}
            title="Click to manage mapping"
          >
            {cell || '-'}
          </span>
        )
      },
    },
    {
      dataField: 'currentUserCount',
      text: 'Users',
      sort: true,
      formatter: (cell, row) => row.hasSyncData ? cell : '-',
    },
    {
      dataField: 'delta',
      text: 'Delta',
      sort: true,
      formatter: (cell, row) => {
        if (!row.hasSyncData || cell == null || cell === 0) return '-'
        const color = cell > 0 ? '#28a745' : '#dc3545'
        const tooltipId = `delta-${row.id}`
        const added = row.usersAdded || []
        const removed = row.usersRemoved || []
        const hasDetails = added.length > 0 || removed.length > 0

        return (
          <>
            <span
              id={tooltipId}
              style={{ color, fontWeight: 'bold', cursor: hasDetails ? 'pointer' : 'default' }}
            >
              {cell > 0 ? `+${cell}` : cell}
            </span>
            {hasDetails && (
              <UncontrolledTooltip target={tooltipId} placement="right" autohide={false}>
                {added.length > 0 && (
                  <div style={{ textAlign: 'left' }}>
                    <strong>Added ({added.length}):</strong>
                    <br />
                    {added.slice(0, 10).join(', ')}
                    {added.length > 10 && ` +${added.length - 10} more`}
                  </div>
                )}
                {removed.length > 0 && (
                  <div style={{ textAlign: 'left', marginTop: added.length > 0 ? '4px' : 0 }}>
                    <strong>Removed ({removed.length}):</strong>
                    <br />
                    {removed.slice(0, 10).join(', ')}
                    {removed.length > 10 && ` +${removed.length - 10} more`}
                  </div>
                )}
              </UncontrolledTooltip>
            )}
          </>
        )
      },
    },
    {
      dataField: 'reconciled',
      text: 'Reconciled',
      sort: true,
      filter: selectFilter({
        options: { true: 'Yes', false: 'No' },
        placeholder: 'Filter...',
      }),
      formatter: (cell) => cell ? 'Yes' : 'No',
    },
    {
      dataField: 'status',
      text: 'Status',
      sort: true,
      filter: selectFilter({
        options: statusOptions,
        placeholder: 'Filter...',
      }),
      formatter: (cell) => (
        <div className="d-flex align-items-center gap-1">
          <span
            style={{
              display: 'inline-block',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              marginRight: '4px',
              backgroundColor: statusColors[cell] || '#6c757d',
            }}
          />
          <span>{statusOptions[cell] || cell}</span>
        </div>
      ),
    },
    {
      dataField: 'statusReason',
      text: 'Reason',
      formatter: (cell) => (cell && cell.length > 50 ? `${cell.substring(0, 50)}...` : cell || '-'),
    },
    {
      dataField: 'actions',
      text: 'Actions',
      isDummyField: true,
      csvExport: false,
      style: { width: '180px', minWidth: '180px' },
      formatter: (cell, row) => {
        const isUpdating = updatingRowId === row.companyId
        const buttons = []

        if (row.status === 'Flagged') {
          buttons.push(
            <button
              key="approve"
              className="btn btn-sm btn-outline-warning mr-1"
              disabled={isUpdating}
              onClick={() => dispatch(approveCompany(row.companyId))}
            >
              {isUpdating ? <span className="spinner-border spinner-border-sm" /> : 'Approve'}
            </button>
          )
        }

        if (row.status === 'Blocked' && row.companyId) {
          buttons.push(
            <button
              key="resolve"
              className="btn btn-sm btn-outline-danger mr-1"
              disabled={isUpdating}
              onClick={() => dispatch(resolveCompany(row.companyId))}
            >
              {isUpdating ? <span className="spinner-border spinner-border-sm" /> : 'Resolve'}
            </button>
          )
        }

        if (row.hasSyncData && row.userEmails && row.userEmails.length > 0) {
          buttons.push(
            <button
              key="export"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => exportUserCsv(row)}
              title="Export user list"
            >
              Export
            </button>
          )
        }

        return buttons.length > 0 ? (
          <div className="d-flex">{buttons}</div>
        ) : null
      },
    },
  ]

  const defaultSorted = [{ dataField: 'companyName', order: 'asc' }]

  return (
    <>
      <PaginationProvider
        pagination={paginationFactory({
          custom: true,
          totalSize: filteredData.length,
          sizePerPageList: [
            { text: '10', value: 10 },
            { text: '20', value: 20 },
            { text: '50', value: 50 },
            { text: '100', value: 100 },
            { text: 'All', value: filteredData.length },
          ],
        })}
      >
        {({ paginationProps, paginationTableProps }) => (
          <ToolkitProvider
            keyField="id"
            data={rows || []}
            columns={columns}
            search
            exportCSV={{
              fileName: 'KeeperRecon.csv',
              onlyExportFiltered: true,
              exportAll: true,
            }}
            onDataChange={(newData) => setFilteredData(newData)}
          >
            {(props) => (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <SearchBar {...props.searchProps} placeholder="Search..." />
                  <div className="d-flex align-items-center">
                    <ExportCSVButton className="btn btn-outline-primary mr-2" {...props.csvProps}>
                      Export CSV
                    </ExportCSVButton>
                    <SizePerPageDropdownStandalone
                      btnContextual="btn-outline-primary"
                      {...paginationProps}
                    />
                  </div>
                </div>

                <BootstrapTable
                  bordered={false}
                  hover
                  condensed
                  defaultSorted={defaultSorted}
                  {...props.baseProps}
                  pagination={paginationFactory()}
                  filter={filterFactory()}
                  filterPosition="top"
                  {...paginationTableProps}
                />

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted small">
                    {(() => {
                      const { page, sizePerPage } = paginationProps
                      const total = filteredData.length
                      const start = (page - 1) * sizePerPage + 1
                      const end = Math.min(page * sizePerPage, total)
                      return `Showing ${start}-${end} of ${total}`
                    })()}
                  </div>
                  <PaginationListStandalone {...paginationProps} />
                </div>
              </div>
            )}
          </ToolkitProvider>
        )}
      </PaginationProvider>

      {mappingModalRow && (
        <CompanyMappingModal
          isOpen={!!mappingModalRow}
          toggle={() => setMappingModalRow(null)}
          keeperEnterpriseId={mappingModalRow.keeperEnterpriseId}
          keeperEnterpriseName={mappingModalRow.keeperEnterpriseName}
        />
      )}
    </>
  )
}

export default KeeperReconTable
